import dayjs from "dayjs";
import "dayjs/locale/es";
import { Bot, GripVertical, User } from "lucide-react";
import { useDraggable } from "@dnd-kit/core";
import { cn } from "@/lib/utils";
import { leadDisplayName } from "@/lib/contact";
import type { KanbanTicket } from "./types";
import {
  TICKET_PRIORITY_COLORS,
  TICKET_PRIORITY_DISPLAY_NAMES,
} from "./constants";

dayjs.locale("es");

type Props = {
  ticket: KanbanTicket;
  onClick: (ticketId: string) => void;
  employeeNameById: Map<string, string>;
  draggable?: boolean;
};

const formatCreatedAt = (createdAt: string) => {
  const d = dayjs(createdAt).locale("es");
  return d.isValid() ? d.format("DD MMM YYYY HH:mm") : "—";
};

export const TicketCard = ({ ticket, onClick, employeeNameById, draggable }: Props) => {
  // `leadPhone` is resolved server-side against the live Lead (logic/ticket.ts),
  // so it already picks the phone, the @username or the id. Re-deriving it here
  // from the ticket's own `customerPhone` copy would read a slot that was frozen
  // at creation and never cleaned by the Lead.phone migration.
  const displayName = leadDisplayName({ name: ticket.leadName });
  const contact = ticket.leadPhone;
  const isAi = ticket.managedBy === "ai";
  const employeeName = ticket.assignedEmployeeId
    ? employeeNameById.get(ticket.assignedEmployeeId) ?? "Asesor"
    : undefined;
  const priorityColor =
    TICKET_PRIORITY_COLORS[ticket.priority] ?? TICKET_PRIORITY_COLORS.medium;
  const priorityLabel =
    TICKET_PRIORITY_DISPLAY_NAMES[ticket.priority] ?? ticket.priority;

  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: ticket.id,
    data: { status: ticket.status },
    disabled: !draggable,
  });

  const dragStyle = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  const dragHandleProps = draggable ? { ...listeners, ...attributes } : undefined;

  return (
    <div
      ref={draggable ? setNodeRef : undefined}
      style={dragStyle}
      className={cn(
        "flex w-full shrink-0 flex-col gap-1 rounded-md border border-gray-200 bg-white p-3 text-left",
        "shadow-sm transition-colors hover:border-palomma-primary/50 hover:bg-gray-50",
        "min-h-11",
        isDragging ? "z-10 cursor-grabbing opacity-70 shadow-lg" : "cursor-pointer"
      )}
      onClick={(e) => {
        if (isDragging) return;
        e.stopPropagation();
        onClick(ticket.id);
      }}
    >
      {/* Row 1: Name + managed-by badge (mirrors LeadCard pattern) */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1">
          {draggable ? (
            <span
              {...dragHandleProps}
              onClick={(e) => e.stopPropagation()}
              className="-ml-1 cursor-grab text-gray-300 hover:text-gray-500 active:cursor-grabbing"
              aria-label="Arrastrar"
              role="button"
            >
              <GripVertical className="size-4" />
            </span>
          ) : null}
          <span className="truncate text-sm font-semibold text-gray-900">
            {displayName}
          </span>
        </div>
        <span
          className={cn(
            "flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium",
            isAi ? "bg-palomma-primary/10 text-palomma-primary" : "bg-emerald-100 text-emerald-700"
          )}
          title={isAi ? "Gestionado por IA" : `Gestionado por ${employeeName}`}
        >
          {isAi ? <Bot className="size-3" /> : <User className="size-3" />}
          <span className="max-w-[8rem] truncate">{isAi ? "IA" : employeeName ?? "Asesor"}</span>
        </span>
      </div>

      {/* Row 2: Phone */}
      <span className="truncate text-xs text-gray-500">{contact}</span>

      {/* Row 3: Category + Priority badges */}
      <div className="flex flex-wrap items-center gap-1">
        {ticket.category ? (
          <span className="w-fit truncate rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-medium text-violet-700">
            {ticket.category}
          </span>
        ) : null}
        <span
          className={cn(
            "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
            priorityColor
          )}
        >
          {priorityLabel}
        </span>
      </div>

      {/* Row 5: Date */}
      <span className="text-[11px] text-gray-400">
        {formatCreatedAt(ticket.createdAt)}
      </span>
    </div>
  );
};
